import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Client } from '../Module/Client.model';
import { ClientService } from '../service/client.service';
import { HttpResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { NotificationService } from '../service/notification.service';

@Component({
  selector: 'app-add-client',
  templateUrl: './add-client.component.html',
  styleUrls: ['./add-client.component.css']
})
export class AddClientComponent implements OnInit {
  clientForm: FormGroup = this.initForm();
  isSubmitting = false;
  client: Client = new Client();

  constructor(
    private fb: FormBuilder,
    private clientService: ClientService,
    private notificationService: NotificationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Watch form changes for validation feedback
    this.clientForm.valueChanges.subscribe(() => {
      this.updateFormValidation();
    });
  }

  private initForm(): FormGroup {
    return this.fb.group({
      lastName: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.pattern(/^[a-zA-ZÀ-ÿ\s\-']+$/)
      ]],
      firstName: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.pattern(/^[a-zA-ZÀ-ÿ\s\-']+$/)
      ]],
      email: ['', [
        Validators.email,
        Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)
      ]],
      phoneNumber: ['']
    });
  }

  private updateFormValidation(): void {
    Object.keys(this.clientForm.controls).forEach(key => {
      const control = this.clientForm.get(key);
      if (control && control.dirty) {
        control.markAsTouched();
      }
    });
  }

  // Calculate form completion progress (0-100)
  getFormProgress(): number {
    if (!this.clientForm) return 0;
    
    // Only count required fields for base progress
    const requiredFields = ['lastName', 'firstName'];
    const totalRequired = requiredFields.length;
    let validRequired = 0;

    requiredFields.forEach(key => {
      const control = this.clientForm.get(key);
      if (control && control.valid && control.touched) {
        validRequired++;
      }
    });

    // Calculate optional fields progress if they are filled
    const optionalFields = ['email', 'phoneNumber'];
    let optionalProgress = 0;

    optionalFields.forEach(key => {
      const control = this.clientForm.get(key);
      if (control && control.value && control.valid) {
        optionalProgress += 0.5; // Each optional field contributes half the weight of a required field
      }
    });

    // Base progress from required fields plus bonus from optional fields
    const baseProgress = (validRequired / totalRequired) * 100;
    const totalProgress = Math.min(100, baseProgress + (optionalProgress * 25)); // Optional fields can add up to 25% more

    return Math.round(totalProgress);
  }

  // Form controls accessor
  get f() {
    return this.clientForm.controls;
  }

  // Get validation error messages
  getErrorMessage(fieldName: string): string {
    const control = this.clientForm.get(fieldName);
    if (!control || !control.errors || !control.touched) return '';

    const errors = control.errors;
    
    switch (fieldName) {
      case 'lastName':
      case 'firstName':
        if (errors['required']) return 'Ce champ est obligatoire';
        if (errors['minlength']) return `Minimum ${errors['minlength'].requiredLength} caractères`;
        if (errors['pattern']) return 'Seuls les lettres, espaces et tirets sont autorisés';
        break;
      
      case 'email':
        if (errors['email'] || errors['pattern']) return 'Format d\'email invalide';
        break;
      
      case 'phoneNumber':
        if (errors['pattern']) return 'Format de téléphone invalide (ex: 06 12 34 56 78)';
        break;
    }
    
    return 'Champ invalide';
  }

  addClient(): void {
    if (this.clientForm.get('lastName')?.invalid || this.clientForm.get('firstName')?.invalid || this.isSubmitting) {
      this.markFormGroupTouched(this.clientForm);
      return;
    }

    // Clean up empty optional fields before submitting
    const formValue = this.clientForm.value;
    const clientData = {
      ...formValue,
      email: formValue.email || null,
      phoneNumber: formValue.phoneNumber || null
    };

    this.isSubmitting = true;
    this.client = { ...this.client, ...clientData };

    this.clientService.addClient(this.client).subscribe({
      next: (response: HttpResponse<Client>) => {
        this.notificationService.success('Client ajouté avec succès');
        this.router.navigate(['/home']);
      },
      error: (error) => {
        console.error('Error:', error);
        this.isSubmitting = false;
        this.notificationService.error(
          error.status === 0 
            ? 'Erreur de connexion au serveur' 
            : 'Erreur lors de l\'ajout du client'
        );
      },
      complete: () => {
        this.isSubmitting = false;
      }
    });
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }
}
